"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PARTNER_WAITING_LABEL = exports.PARTNER_OWNER_LABEL = exports.PARTNER_LANE_LABEL = exports.PARTNER_LANE = void 0;
exports.partnerOrderSummary = partnerOrderSummary;
exports.partnerStages = partnerStages;
exports.partnerLanes = partnerLanes;
exports.partnerOrderView = partnerOrderView;
const orderJourney_1 = require("./orderJourney");
/**
 * THE ORDER AS A PAYMENTS PARTNER MAY SEE IT.
 *
 * An ISO often sources the merchant, boards them, and hands RubyTill the
 * business — and until now their Orders screen showed them a card application
 * and nothing else. They could not answer the question the merchant asks them
 * first: where is my order. So the partner gets the SAME rail the super admin
 * reads, every stage of it, and the transparency is the point.
 *
 * WHAT THEY MAY CHANGE IS A DIFFERENT QUESTION FROM WHAT THEY MAY SEE, and
 * this module answers only the second. One lane is theirs to work — `card`,
 * the merchant account: document verification, the selection, and their own
 * ISO processing. Every other lane is an observation. `editable` on a stage
 * says which, so a screen cannot decide it differently from the server.
 *
 * REDACTION IS AN ALLOWLIST, never a delete. Nothing is spread in from a
 * resolved stage or an order row; every field below is named. A stage gaining
 * an `evidence` string or an internal note next year therefore cannot leak to
 * a partner by default — it has to be added here on purpose.
 *
 * SPECIFICALLY WITHHELD, and why:
 *   · provisioning codes, terminal override codes, owner PINs — these are
 *     credentials. A partner has no use for one and a leaked one is a device
 *     somebody else can claim.
 *   · what anything cost, what was collected, what the deal pays — RubyTill's
 *     commercials with the merchant. The partner's own commission is their
 *     Commissions page, off the order entirely.
 *   · WHO at RubyTill ticked a manual stage. A partner is owed the fact and
 *     the time, not our staff directory.
 *   · the access log, internal notes, and any thread message not addressed to
 *     them — already filtered upstream by `shapeApplication`/`visibleMessages`.
 */
/** The one lane a partner works rather than watches. */
exports.PARTNER_LANE = 'card';
/**
 * THE SAME LANE TITLES THE OPERATOR RAIL USES.
 *
 * Deliberately word-for-word with super-admin's `LANE_TITLE`: a partner and an
 * operator discussing an order on the phone must be looking at the same four
 * names. Softening them for the partner ("Card readers", "Tills") gave two
 * vocabularies for one screen.
 */
exports.PARTNER_LANE_LABEL = {
    main: 'The order',
    card: 'Card processing',
    payment_devices: 'Payment devices',
    pos_devices: 'POS devices',
    accessories: 'Peripherals & accessories',
    // Retired lanes. Kept because a journey snapshot written before the fork
    // still names them, and a lane with no label renders as `undefined`.
    merchant: 'Card processing',
    fulfilment: 'Hardware',
};
/** Whose move it is, said to a partner rather than to an operator. */
exports.PARTNER_OWNER_LABEL = {
    system: 'Automatic',
    rubytill: 'RubyTill',
    customer: 'The merchant',
    iso: 'You',
    warehouse: 'RubyTill warehouse',
};
function partnerOrderSummary(view) {
    const mine = view.lanes.find((l) => l.mine);
    /*
      WHAT THE ORDER IS WAITING ON — the first open stage in the whole list, not
      the first open stage on the spine. Lanes run at the same time, so the spine
      is often finished while three lanes are mid-flight; reading the spine alone
      reported "Stock allocated" on an order whose readers were being keyed.
      A blocked stage outranks an active one: it is the one somebody must act on.
    */
    const now = view.stages.find((s) => s.state === 'blocked')
        || view.stages.find((s) => s.state === 'active');
    const payment = view.devices.filter((d) => d.lane === 'payment');
    return {
        orderId: view.orderId,
        orderNo: view.orderNo,
        placedAt: view.placedAt,
        pct: view.pct,
        minePct: mine?.pct ?? 0,
        mineNow: mine?.now,
        mineNowOwner: mine?.nowOwner,
        now: now?.label,
        nowOwner: now?.owner,
        blockers: view.blockers.length,
        stuck: view.stages.some((s) => s.state === 'blocked'),
        paymentDevices: payment.length,
        keyedDevices: payment.filter((d) => d.terminalId).length,
    };
}
exports.PARTNER_WAITING_LABEL = {
    iso: 'Your move',
    tenant: 'Waiting on the merchant',
    processor: 'At the processor',
    rubytill: 'Waiting on RubyTill',
    nobody: 'Nothing outstanding',
};
/** Stage ids a partner may actually move, via their own routes. */
const PARTNER_ACTIONABLE = new Set(['compliance', 'merchant_assigned', 'var_sheet', 'underwriting']);
/**
 * THE SAME FACT, ADDRESSED TO THE PARTNER INSTEAD OF ABOUT THEM.
 *
 * Two of the resolver's sentences are written from RubyTill's chair and read
 * wrong from the partner's:
 *
 *   · a manual tick says "Marked done by <person>". A partner is owed that it
 *     happened, not who at RubyTill did it.
 *   · the assignment says "With Northbridge." — which, read by Northbridge, is
 *     being told about themselves in the third person. It becomes "You are the
 *     selected merchant processor."
 *
 * The FACT is never changed here, only the voice. `viewerIsoId` is what makes
 * that safe: the rewrite applies only when the assignment really is this
 * reader's, so a partner looking at an application assigned to somebody else
 * is never told it is theirs.
 */
const revoice = (s, viewerIsoId) => {
    if (s.assertedBy && /^Marked done by /.test(s.detail)) {
        return s.def.owner === 'iso' ? 'Marked done by you.' : 'Marked done by RubyTill.';
    }
    if (s.def.id === 'merchant_assigned' && s.state === 'done' && viewerIsoId && s.evidence === 'merchant application') {
        return 'You are the selected merchant processor.';
    }
    return s.detail;
};
/**
 * A hint written for whoever picks the partner is not a hint for the partner
 * who was picked. "A person decides which ISO suits this sector" is our
 * selection note; to them the useful thing is what being selected means.
 */
const PARTNER_HINT = {
    merchant_assigned: 'RubyTill chose you for this merchant. Everything on this lane is yours from here.',
};
const LANE_ORDER = ['main', 'card', 'payment_devices', 'pos_devices', 'accessories'];
function partnerStages(resolved, viewerIsoId, unknown = []) {
    return resolved.map((s) => {
        const lane = (s.def.lane || 'main');
        const mine = lane === exports.PARTNER_LANE;
        return {
            id: s.def.id,
            label: s.labelOverride || s.def.label,
            lane,
            owner: s.ownerOverride || s.def.owner,
            state: s.state,
            detail: unknown.includes(s.def.id) && s.state !== 'done'
                ? 'RubyTill has not shared this step yet.'
                : revoice(s, viewerIsoId),
            /*
              HINTS ARE INTERNAL INSTRUCTIONS, and only theirs cross over.
      
              A stage hint is written for whoever has to do the work — "a STATUS view
              here, the work itself is done in device management", "the provisioning
              code it claims itself with", "give the owner their access and PIN". On
              the partner's own lane that is exactly the guidance they need. On a
              lane they only watch it is our runbook, so the label and the live
              detail carry it instead.
            */
            hint: mine ? (PARTNER_HINT[s.def.id] || s.hintOverride || s.def.hint) : undefined,
            at: s.at,
            activeSince: s.activeSince,
            age: s.age,
            blocksDispatch: s.blocksDispatch,
            mine,
            editable: mine && PARTNER_ACTIONABLE.has(s.def.id),
        };
    });
}
function partnerLanes(stages) {
    const present = LANE_ORDER.filter((l) => stages.some((s) => s.lane === l));
    return present.map((id) => {
        const mine = stages.filter((s) => s.lane === id);
        const counted = mine.filter((s) => s.state !== 'skipped');
        const done = counted.filter((s) => s.state === 'done').length;
        const open = mine.find((s) => s.state === 'active' || s.state === 'blocked');
        const blocked = mine.some((s) => s.state === 'blocked');
        return {
            id,
            label: exports.PARTNER_LANE_LABEL[id],
            // A lane with nothing to do is 100% rather than 0% — everything on it is
            // skipped, which is finished, not outstanding.
            pct: counted.length === 0 ? 100 : Math.round((done / counted.length) * 100),
            done,
            total: counted.length,
            now: open?.label,
            nowOwner: open?.owner,
            state: blocked ? 'blocked' : open ? 'active' : counted.length === 0 || done === counted.length ? 'done' : 'todo',
            mine: id === exports.PARTNER_LANE,
        };
    });
}
/**
 * The whole projection. Callers gather the facts (they own the records) and
 * hand them in with the resolved journey; what a partner may see is decided
 * here and only here.
 */
function partnerOrderView(resolved, facts) {
    const stages = partnerStages(resolved, facts.viewerIsoId, facts.unknown);
    const { done, total } = (0, orderJourney_1.journeyProgress)(resolved);
    const origin = facts.origin || 'unknown';
    return {
        orderId: facts.orderId,
        orderNo: facts.orderNo,
        placedAt: facts.placedAt,
        status: facts.status,
        pct: total === 0 ? 0 : Math.round((done / total) * 100),
        business: facts.business || {},
        location: facts.location,
        origin,
        originLabel: orderJourney_1.CUSTOMER_ORIGIN_LABEL[origin],
        deal: facts.deal,
        lines: facts.lines || [],
        devices: facts.devices || [],
        stages,
        lanes: partnerLanes(stages),
        blockers: stages
            .filter((s) => s.blocksDispatch && s.state !== 'done' && s.state !== 'skipped')
            .map((s) => ({ id: s.id, label: s.label, detail: s.detail, owner: s.owner, mine: s.mine })),
    };
}
//# sourceMappingURL=partnerOrderView.js.map